import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { AppPhase, SupervisorAction } from '../../../models/communication/app-enums.model';
import { FlagsState, SupervisorState } from '../../../models/communication/app-state.model';

type EnabledActionsType = Partial<Record<SupervisorAction, boolean>>;

/**
 * A collection of buttons that emit different `SupervisorAction` values.
 * The `supervisorActions` is a list of actions that needs to be visible.
 *
 * Supervisor rules about disabling the buttons are defined in the following
 * [table](https://troniusgaming.atlassian.net/wiki/spaces/Product/pages/471138344/iDAT?force_transition=38d35250-49a1-4b62-a559-fae9a6b2b56e).
 */
@Component({
    selector: 'app-supervisor-actions-roulette',
    imports: [CommonModule],
    templateUrl: './supervisor-actions-roulette.component.html',
    styleUrl: './supervisor-actions-roulette.component.scss'
})
export class SupervisorActionsRouletteComponent implements OnInit {
    @Input()
    appPhase?: AppPhase;

    @Input()
    flagsState: FlagsState = {};

    @Input()
    supervisorActions: SupervisorAction[] = [];

    @Input()
    supervisorState?: SupervisorState;

    @Output()
    readonly actionClicked = new EventEmitter<SupervisorAction>();

    protected enabledActions!: EnabledActionsType;

    protected readonly AppPhase = AppPhase;
    protected readonly SupervisorAction = SupervisorAction;

    protected get hasSecurityDoorsOpen(): boolean {
        return (this.supervisorState?.securityDoorsOpen?.length ?? 0) > 0;
    }

    ngOnInit(): void {
        if (this.appPhase === undefined) {
            // eslint-disable-next-line no-console
            console.error('The appPhase input must be provided');
            return;
        }

        this.initEnableActions();
    }

    /**
     * Initializes the `enabledActions` property by mapping `supervisorActions`
     * to an object where each key (SupervisorAction) is set to `true`.
     *
     * ```typescript
     * // Transforms the array:
     * [SupervisorAction.VoidGame, SupervisorAction.ChangeGame, SupervisorAction.CloseTable]
     * // into the object:
     * {[SupervisorAction.VoidGame]: true, [SupervisorAction.ChangeGame]: true, [SupervisorAction.CloseTable]: true}
     * ```
     */
    private initEnableActions(): void {
        this.enabledActions = this.supervisorActions.reduce<EnabledActionsType>(
            (actionMap, action) => ({ ...actionMap, [action]: true }),
            {}
        );
    }
}
